import { api, type EpisodesResponse } from '../lib/api';
import { useResource } from '../lib/useResource';
import { formatNumber } from '../lib/dataState';
import { formatInstant } from '../lib/format';

/**
 * Episódios do Loyalty Cast.
 *
 * A coluna que a tela precisa acertar é a distinção entre **processar** e
 * **publicar**. Um episódio sob embargo não é um bloqueio: é trabalho legítimo
 * em andamento — transcrever, mapear cortes e preparar derivados é permitido, e
 * é isso que faz a equipe trabalhar adiantada. O que não pode é o recorte ir ao
 * ar antes do episódio.
 *
 * Mostrar só "não publica" faria a operação parar de preparar; mostrar só "pode
 * processar" esconderia o embargo. Por isso as duas colunas aparecem juntas,
 * por classe de conteúdo.
 */

const RELEASE_LABEL: Record<string, string> = {
  recorded_embargo: 'Gravado, sob embargo',
  scheduled: 'Agendado',
  public: 'Público',
  reusable: 'Liberado para reuso',
  hold: 'Retido',
};

const CLASS_LABEL: Record<string, string> = {
  teaser: 'Teaser',
  cut: 'Corte',
  quote: 'Citação',
};

export function Episodes() {
  const resource = useResource<EpisodesResponse>(() => api.episodes(), []);

  if (resource.kind === 'loading') {
    return (
      <div className="text-sm" aria-busy="true">
        <h1 className="text-2xl font-bold">Loyalty Cast</h1>
        <p className="mt-2 text-ink-muted">Carregando episódios…</p>
      </div>
    );
  }

  if (resource.kind === 'error') {
    return (
      <div className="max-w-3xl space-y-4 text-sm">
        <h1 className="text-2xl font-bold">Loyalty Cast</h1>
        <div className="mt-4 rounded border border-danger-border bg-danger-surface p-4">
          <p className="font-medium">Não foi possível carregar os episódios.</p>
          <p className="mt-1">{resource.message}</p>
          <p className="mt-2 text-xs text-ink-faint">
            Nenhum episódio aparece como liberado aqui: não saber o estado de release nunca é o
            mesmo que estar liberado. Código: {resource.code}
          </p>
        </div>
      </div>
    );
  }

  const { data } = resource;

  return (
    <div className="space-y-4 text-sm">
      <header>
        <h1 className="text-2xl font-bold">Loyalty Cast</h1>
        <p className="mt-1 text-ink-muted">
          {formatNumber(data.total)} episódio(s). Preparar derivado sob embargo é permitido;
          publicar não.
        </p>
      </header>

      {data.total === 0 ? (
        <p className="mt-6 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
          Nenhum episódio ingerido. Enquanto não houver ingestão, todo derivado do Loyalty Cast é
          negado pelo portão de release: status desconhecido falha fechado.
        </p>
      ) : (
        <ul className="mt-6 space-y-4">
          {data.episodes.map((ep) => (
            <li
              key={ep.episode_id}
              className="rounded-card border border-line bg-card p-4 shadow-card"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium">
                  {ep.guest_name} · {ep.company_name}
                </span>
                <span className="text-xs uppercase tracking-wide text-ink-muted">
                  {RELEASE_LABEL[ep.release_status] ?? ep.release_status}
                </span>
              </div>

              <p className="mt-1 text-xs text-ink-faint">
                {formatNumber(ep.cut_count)} corte(s) mapeado(s)
                {ep.scheduled_publish_at
                  ? ` · vai ao ar em ${formatInstant(ep.scheduled_publish_at)}`
                  : ''}
              </p>

              {ep.prelaunch_start_at ? (
                <p className="mt-2 text-xs text-ink-muted">
                  Janela de pré-lançamento: {formatInstant(ep.prelaunch_start_at)} até{' '}
                  {ep.prelaunch_end_at ? formatInstant(ep.prelaunch_end_at) : 'sem fim declarado'} ·
                  classes autorizadas:{' '}
                  {ep.prelaunch_authorized_classes.length > 0
                    ? ep.prelaunch_authorized_classes.join(', ')
                    : 'nenhuma'}
                </p>
              ) : null}

              {ep.rights_exceptions.length > 0 ? (
                <ul className="mt-2 space-y-1">
                  {ep.rights_exceptions.map((e, i) => (
                    <li
                      key={`${e.scope}-${i}`}
                      className={`text-xs ${e.effect === 'deny' ? 'text-danger-ink' : 'text-ok-ink'}`}
                    >
                      Exceção {e.effect === 'deny' ? 'de proibição' : 'de liberação'} para &quot;
                      {e.scope}&quot;: {e.reason} (vale até {formatInstant(e.valid_until)})
                    </li>
                  ))}
                </ul>
              ) : null}

              <div className="mt-3 overflow-x-auto">
                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-line text-left text-ink-muted">
                      <th className="py-1 pr-4 font-medium">Classe</th>
                      <th className="py-1 pr-4 font-medium">Preparar</th>
                      <th className="py-1 pr-4 font-medium">Publicar</th>
                      <th className="py-1 font-medium">Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ep.release_by_class.map((c) => (
                      <tr key={c.content_class} className="border-b border-line">
                        <td className="py-1 pr-4">
                          {CLASS_LABEL[c.content_class] ?? c.content_class}
                        </td>
                        <td className="py-1 pr-4">
                          <span className={c.can_process ? 'text-ok-ink' : 'text-danger-ink'}>
                            {c.can_process ? 'permitido' : 'não'}
                          </span>
                        </td>
                        <td className="py-1 pr-4">
                          <span className={c.can_publish ? 'text-ok-ink' : 'text-danger-ink'}>
                            {c.can_publish ? 'permitido' : 'não'}
                          </span>
                        </td>
                        <td className="py-1 text-ink-faint">{c.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
